import z from "zod";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { productGridKey } from "../products/useGetAllproduct";
import { getProductScoresKey } from "../olapScores/useGetProductScores";

export const DistributionSchema = z.object({
    "@id": z.string().optional(),
    id: z.number(),
    name: z.string(),
    distributionReferenceId: z.number(),
});

export type Distribution = z.infer<typeof DistributionSchema>;

export type DistributionWrite = {
    id?: number;
    name: string;
    distributionReferenceId: number;
    ownedBy?: IRI;
};

export const distributionsKey = "distributions";

export const useDistributions = (params?: { enabled?: boolean }) => {
    const query = useQuery({
        enabled: params?.enabled ?? true,
        queryKey: [distributionsKey],
        queryFn: async () => {
            return await loggedInApiCall.get("/api/distributions", {
                params: { itemsPerPage: 100 },
            });
        },
        select: (request) => {
            const members = request.data["hydra:member"] ?? request.data.member ?? [];
            return zodParseWithLog(members, DistributionSchema.array(), "useDistributions");
        },
    });

    return {
        distributions: query.data ?? [],
        ...query,
    };
};

export const useUpsertDistribution = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (distribution: DistributionWrite) => {
            if (distribution.id !== undefined) {
                return await loggedInApiCall.put(`/api/distributions/${distribution.id}`, distribution);
            }

            return await loggedInApiCall.post("/api/distributions", distribution);
        },
        onSuccess: () => {
            queryClient.invalidateQueries([distributionsKey]);
            queryClient.invalidateQueries([productGridKey]);
            queryClient.invalidateQueries([getProductScoresKey]);
        },
    });
};

export const useDeleteDistribution = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: number) => {
            return await loggedInApiCall.delete(`/api/distributions/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries([distributionsKey]);
            queryClient.invalidateQueries([productGridKey]);
            queryClient.invalidateQueries([getProductScoresKey]);
        },
    });
};
